import React, { useEffect, useState } from 'react';
import { useDataStore } from 'files/StateManagement';

import Abstract from 'classes/Abstract.js';
import { AltBadge } from 'views/Main.js';
import AltFieldMapper, { validateRequiredFields } from 'views/AltFieldMapper';
import Appearance from 'styles/Appearance.js';
import AnimatedElement from 'views/AnimatedElement.js';
import Button from 'views/Button.js';
import CollapseContainer from 'views/CollapseContainer.js';
import Layer, { LayerItem } from 'structure/Layer.js';
import Panel, { PanelContentContainer } from 'structure/Panel.js';
import Request from 'files/Request.js';
import Utils from 'files/Utils.js';

const getPartnershipTiers = () => {
    return [{
        benefits: [
            'Dedicated partner success manager',
            'Co-branded marketing materials',
            'Quarterly strategy sessions',
        ],
        commission: 7,
        description: 'Ideal for growing practices and small retirement communities',
        id: 'growth',
        range: '10-500',
        title: 'Growth'
    },{
        benefits: [
            'Priority support channel',
            'Custom landing pages',
            'Monthly strategy sessions',
            'Early access to new features',
        ],
        commission: 8,
        description: 'For established businesses with strong client networks ',
        id: 'professional',
        range: '500-5,000',
        title: 'Professional'
    },{
        benefits: [
            'Executive sponsor',
            'Custom integration options',
            'Bi-weekly strategy sessions',
            'Co-marketing opportunities',
            'Annual partner summit invitation',
        ],
        commission: 9,
        description: 'For large organizations and regional service providers',
        id: 'enterprise',
        range: '5,000-20,000+',
        title: 'Enterprise'
    }];
}

export const NewPartnershipApplication = ({ tier }, { abstract, index, options, utils }) => {

    const layerID = 'new_partnership_application';

    const [edits, setEdits] = useState({});
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const { referredByUserCode } = useDataStore();

    const getFields = () => {
        return [{
            collapsed: null,
            key: 'details',
            lastItem: true,
            style: {
                border: 'none'
            },
            items: [{
                component: 'textfield',
                key: 'first_name',
                onChange: text => onUpdateTarget({ first_name: text }),
                title: 'First Name',
                value: edits.first_name
            },{
                component: 'textfield',
                key: 'last_name',
                onChange: text => onUpdateTarget({ last_name: text }),
                title: 'Last Name',
                value: edits.last_name
            },{
                component: 'textfield',
                key: 'email_address',
                onChange: text => onUpdateTarget({ email_address: text }),
                title: 'Email Address',
                value: edits.email_address
            },{
                component: 'textfield',
                key: 'phone_number',
                onChange: text => onUpdateTarget({ phone_number: text }),
                props: {
                    format: 'phone_number'
                },
                title: 'Phone Number',
                value: edits.phone_number
            },{
                component: 'list',
                items: getPartnershipTiers(),
                key: 'tier_id',
                onChange: item => onUpdateTarget({ tier_id: item?.id }),
                title: 'Partnership Tier',
                value: getPartnershipTiers().find(item => item.id === edits.tier_id)?.title
            },{
                component: 'textfield',
                key: 'profession',
                onChange: text => onUpdateTarget({ profession: text }),
                title: 'Profession',
                value: edits.profession
            },{
                component: 'textfield',
                key: 'referral_code',
                onChange: text => onUpdateTarget({ referral_code: text }),
                required: false,
                title: 'Referral Code (Optional)',
                value: edits.referral_code
            },{
                component: 'textview',
                key: 'description',
                onChange: text => onUpdateTarget({ description: text }),
                title: 'Tell Us About Your Use-Case',
                value: edits.description
            }]
        }];
    }

    const onSubmitClick = async () => {
        try {

            // verify that required fields were filled out
            await validateRequiredFields(getFields);

            // set loading flag and submit request to server
            setLoading('submit');
            await Request.post(utils, '/users/', {
                ...edits,
                type: 'new_partnership_application'
            });

            // show confirmation alert
            setLoading(false);
            utils.alert.show({
                title: 'All Done!',
                message: 'Your partnership application has been submitted. We will reach out within the next 48 hours with information regarding your application status.',
                onClick: setLayerState.bind(this, 'close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue submitting your partnership application. ${e.message || 'An unknown error occurred'}`,
                origin: 'new_partnership_application.on_submit_click'
            });
        }
    }


    const onUpdateTarget = props => {
        setEdits(edits => ({ ...edits, ...props }));
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        },{
            color: 'primary',
            id: 'submit',
            loading: loading === 'submit',
            onClick: onSubmitClick,
            text: 'Submit'
        }];
    }

    const setupEditing = () => {
        setEdits({
            ...abstract.object,
            referral_code: referredByUserCode,
            tier_id: tier?.id
        });
    }

    useEffect(() => {
        setupEditing();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            removePadding: true
        }}>
            <div style={{
                borderBottom: `1px solid ${Appearance.colors.divider()}`,
                padding: 18,
                width: '100%'
            }}>
                <LayerItem 
                collapsed={null}
                lastItem={true}
                title={'Partnership Program'}>
                    <span style={{
                        ...Appearance.textStyles.defaultText()
                    }}>{`Once your application has been submitted, it will be carefully reviewed by a member of our team. We take the time to evaluate each submission to ensure it meets our standards and requirements. After the review process is complete, you will be notified of the outcome via the contact information you provided. We appreciate your interest and thank you for taking the time to apply.`}</span>
                </LayerItem>
            </div>
            <AltFieldMapper
            fields={getFields()}
            utils={utils} />
            <div style={{
                borderTop: `1px solid ${Appearance.colors.divider()}`,
                padding: 18,
                width: '100%'
            }}>
                <span style={{
                    color: Appearance.colors.subText(),
                    display: 'flex',
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: 1.75,
                    width: '100%'
                }}>{`By submitting your application, you acknowledge and agree that you are representing yourself as an independent contractor and not as an employee, agent, or representative of LifeWipe Inc. Submission of this application does not establish an employment relationship of any kind. You are solely responsible for complying with any applicable laws, regulations, or tax obligations related to your independent contractor status. Additionally, you confirm that you have read, understood, and agree to the Terms of Service and all other applicable legal agreements. Please review them carefully before submitting your application`}</span>
            </div>
        </Layer>
    )
}

export const Partnerships = ({ utils }) => {

    const { system, user } = useDataStore();

    const onApplyForTierClick = tier => {
        
        // prompt user to signup for an account or login to an existing account is no user is found
        user ? startApplication(tier, user) : utils.user.request.login({}, startApplication.bind(this, tier));
    }

    const onScheduleDemoClick = () => {
        utils.layer.open({
            Component: ScheduleDemo,
            id: 'schedule_demo'
        });
    }

    const getBenefits = () => {

        let items = [{
            benefits: [
                'Weekly commission payments',
                'Earn for years from a single referral',
                'High customer retention rate',
                'Increasing commission tiers as you grow',
            ],
            highlight: 'Partners earn between 2-10% commission on all referred subscriptions.',
            image: 'images/partnership-revenue-icon.png',
            title: 'Recurring Revenue'
        },{
            benefits: [
                'Offer a valuable complementary service',
                'Position yourself as a forward-thinking advisor',
                'Increase client touchpoints and engagement',
                'Solve a real problem clients don\'t know they have'
            ],
            highlight: 'Partners report stronger client relationships after offering LifeWipe.',
            image: 'images/partnership-clients-icon.png',
            title: 'Enhanced Relationships'
        },{
            benefits: [
                'Ready-to-use marketing materials',
                'Sales training and conversation guides',
                'Dedicated partner success manager',
                'Technical support for you and your clients'
            ],
            highlight: 'Partners receive everything needed to succeed with minimal time investment.',
            image: 'images/partnership-support-icon.png',
            title: 'Comprehensive Support'
        }];
    
        return (
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            useContentDivider={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
            }}>
                <PanelContentContainer
                body={`Partnering with LifeWipe offers numerous advantages beyond just commission earnings—it's an opportunity to elevate your brand, deepen client trust, and stay ahead of the curve in digital estate planning.`}
                title={'LifeWipe Partner Benefits'} />
                <div 
                className={'row w-100 m-0'}
                style={{
                    maxWidth: 1650
                }}>
                    {items.map((item, index) => {
                        return (
                            <div 
                            className={'col-12 col-xl-4 p-0 p-xl-2 mx-0 mb-0 mt-3'}
                            key={index}>
                                <div style={{
                                    backgroundColor: Appearance.colors.panelBackground(),
                                    borderRadius: system.window.isMobile ? 0 : 16,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    width: '100%'
                                }}>
                                    <div  
                                    className={'p-4'}
                                    style={{
                                        alignItems: 'center',
                                        display: 'flex',
                                        flexDirection: 'row',
                                        textAlign: 'left',
                                        width: '100%',
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            width: '100%'
                                        }}>
                                            <span style={{
                                                ...Appearance.textStyles.heavyHeader(),
                                                fontFamily: 'Zen Dots',
                                                whiteSpace: 'normal'
                                            }}>{item.title.toUpperCase()}</span>
                                            <span style={{
                                                ...Appearance.textStyles.defaultText(),
                                                whiteSpace: 'normal'
                                            }}>{item.highlight}</span>
                                        </div>
                                        <img 
                                        className={'d-none d-xl-flex'}
                                        src={item.image}
                                        style={{
                                            height: 50,
                                            marginLeft: 16,
                                            objectFit: 'contain',
                                            width: 50
                                        }} />
                                    </div>
                                    <div style={{
                                        flexGrow: 1,
                                        width: '100%'
                                    }}>
                                        {item.benefits.map((text, index) => {
                                            return (
                                                <div 
                                                key={index}
                                                style={{
                                                    alignItems: 'center',
                                                    borderTop: `1px solid ${Appearance.colors.divider()}`,
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                    padding: 12,
                                                    textAlign: 'left',
                                                    width: '100%'
                                                }}>
                                                    <img 
                                                    src={window.theme === 'dark' ? 'images/checkmark-badge-dark.png' : 'images/checkmark-badge-light.png'}
                                                    style={{
                                                        height: 25,
                                                        marginRight: 8,
                                                        objectFit: 'contain',
                                                        width: 25 
                                                    }} />
                                                    <span style={{
                                                        ...Appearance.textStyles.defaultTextSmall()
                                                    }}>{text}</span>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </AnimatedElement>
        )
    }

    const getContainerButtons = () => {
        return [{
            color: 'dark',
            id: 'schedule',
            onClick: onScheduleDemoClick,
            text: 'Schedule a Demo'
        },{
            color: 'primary',
            id: 'apply',
            onClick: onApplyForTierClick.bind(this, null),
            text: 'Apply Now'
        }];
    }
    
    const getDescription = () => {

        let items = [{
            description: 'Use your unique referral link or code to refer customers to LifeWipe. We provide marketing materials and support to help you succeed.',
            image: 'images/partnerships-refer-users.png',
            title: 'Refer Customers'
        },{
            description: 'After 3 months, we assess your average monthly active users and determine your partnership tier and commission rate.',
            image: 'images/partnerships-refer-assess.png',
            title: '3 Month Assessment'
        },{
            description: 'Receive weekly commission payments based on your tier. Commissions are calculated as a percentage of the subscription revenue from your referred customers, allowing you to get paid faster.',
            image: 'images/partnerships-refer-commission.png',
            title: 'Earn Commissions'
        },{
            description: 'Every 3 months, we reassess your partnership tier based on your average monthly active users during that period. Your tier can move up or down based on performance, allowing you to earn higher commissions as your referrals grow.',
            image: 'images/partnerships-refer-reassess.png',
            title: 'Reassessments'
        }];

        return (
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            useContentDivider={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                textAlign: 'center',
                width: '100%'
            }}>
                <PanelContentContainer
                body={`Our partnership program is built on three core principles: transparency, fairness, and meaningful rewards—no matter the size of your business. Whether you're a solo practitioner, a boutique agency, or part of a large advisory firm, we've designed this program to scale with your success.`}
                title={'How The Partnership Program Works'} />
                <div className={'row w-100 mx-0 my-0 mt-xl-3'}>
                    {items.map((item, index) => {
                        if(Utils.isMobile() === true) {
                            return (
                                <div 
                                className={'col-12 p-0 mt-2'}
                                key={index}>
                                    <div 
                                    className={'p-4'}
                                    style={{
                                        backgroundColor: Appearance.colors.panelBackground(),
                                        display: 'flex',
                                        flexDirection: 'column',
                                        height: '100%',
                                        textAlign: 'left',
                                        width: '100%'
                                    }}>
                                        <span style={{
                                            ...Appearance.textStyles.heavyHeader(),
                                            fontFamily: 'Zen Dots',
                                            marginBottom: 8,
                                            whiteSpace: 'normal'
                                        }}>{item.title.toUpperCase()}</span>
                                        <span style={{
                                            ...Appearance.textStyles.defaultText(),
                                            flexGrow: 1,
                                            whiteSpace: 'normal'
                                        }}>{item.description}</span>
                                    </div>
                                </div>
                            )
                        }
                        return (
                            <div 
                            className={'col m-0 p-2'}
                            key={index}>
                                <div 
                                className={'p-4'}
                                style={{
                                    ...Appearance.styles.unstyledPanel(),
                                    alignItems: 'flex-start',
                                    border: 'none',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    height: '100%',
                                    textAlign: 'left',
                                    width: '100%'
                                }}>
                                    <img 
                                    src={item.image}
                                    style={{
                                        height: 50,
                                        marginBottom: 16,
                                        objectFit: 'contain',
                                        width: 50
                                    }} />
                                    <span style={{
                                        ...Appearance.textStyles.heavyHeader(),
                                        fontFamily: 'Zen Dots',
                                        marginBottom: 4,
                                        whiteSpace: 'normal'
                                    }}>{item.title.toUpperCase()}</span>
                                    <span style={{
                                        ...Appearance.textStyles.defaultText(),
                                        flexGrow: 1,
                                        whiteSpace: 'normal'
                                    }}>{item.description}</span>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </AnimatedElement>
        )
    }

    const getFAQ = () => {

        let items = [{
            body: 'Simply click the "Apply Now" button on this page and fill out the application form. We\'ll review your application and get back to you within 2 business days.',
            title: 'How do I join the partnership program?'
        },{
            body: `We're looking for partners who are passionate about digital legacy protection and have an existing network of potential customers. Specific requirements vary by tier, but generally we require a minimum number of monthly active users.`,
            title: 'What are the requirements to become a partner?'
        },{
            body: 'Commissions are calculated as a percentage of the subscription revenue from your referred customers. The percentage varies based on your partnership tier. Commissions are paid weekly via direct deposit or PayPal, allowing you to receive your earnings much faster than traditional monthly payouts.',
            title: 'How are commissions calculated and paid?'
        },{
            body: 'We provide you with a unique referral link and QR code that automatically tracks all sign-ups back to your account. You can also track your referrals in real-time via our partner dashboard.',
            title: 'How do you track referrals?'
        },{
            body: 'We provide a comprehensive suite of marketing materials including brochures, email templates, social media assets, presentation decks, and educational content. Higher tier partners receive customized materials co-branded with their business. All partners have access to our resource library which is updated regularly with new content.',
            title: 'What marketing materials do you provide?'
        },{
            body: 'Veterans receive a 10% discount on all subscription plans. This applies to both partners who are veterans and customers who are veterans. Partners who are veterans will receive the discount on their partnership fees, and they can offer the same discount to their veteran customers. Verification is simple through standard military ID or discharge documentation. This discount is our way of honoring those who have served while providing essential digital legacy protection for veterans who may have limited family connections.',
            title: 'How does the verteran discount work?'
        },{
            body: 'Partners in our program are considered independent contractors (1099) for tax purposes, not employees. This means you\'ll receive a 1099 form at the end of the year for your tax filing. You\'re responsible for your own tax payments, including self-employment taxes. We recommend consulting with a tax professional to understand your specific tax obligations. The benefit of this arrangement is greater flexibility and the ability to write off business expenses related to your partnership activities.',
            title: 'What is my tax status as a partner?'
        }];

        return (
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <div style={{
                    alignItems: 'center',
                    borderRadius: 10,
                    display: 'flex',
                    flexDirection: 'column',
                    maxWidth: 1250,
                    overflow: 'hidden',
                    width: '100%'
                }}>
                    <PanelContentContainer 
                    body={`We've put together answers to some of the most common questions we receive from potential partners. Whether you're curious about how commissions work, how client referrals are tracked, or what kind of support you'll receive, this section covers the key details to help you get started with confidence. If you don't see your question here, feel free to reach out—we're always happy to chat.`}
                    title={'Frequently Asked Questions'} />
                    <div className={'row m-0 p-4 p-xl-0'}>
                        {items.map((item, index) => {
                            if(Utils.isMobile() === true) {
                                return (
                                    <div
                                    className={`col-12 p-0 mt-${index === 0 ? 0 : 3} mx-0`}
                                    key={index}>
                                        <div style={{
                                            ...Appearance.styles.unstyledPanel(),
                                            border: 'none',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            minWidth: 0,
                                            height: '100%',
                                            width: '100%'
                                        }}>
                                            <CollapseContainer 
                                            defaultValue={true}
                                            header={(
                                                <span style={{
                                                    ...Appearance.textStyles.header(),
                                                    display: 'block',
                                                    fontFamily: 'Zen Dots',
                                                    padding: 16,
                                                    whiteSpace: 'normal'
                                                }}>{item.title.toUpperCase()}</span>
                                            )}>
                                                <div style={{
                                                    borderTop: `1px solid ${Appearance.colors.divider()}`,
                                                    padding: 16,
                                                    width: '100%'
                                                }}>
                                                    <span style={{
                                                        ...Appearance.textStyles.defaultText(),
                                                        whiteSpace: 'normal'
                                                    }}>{item.body}</span>
                                                </div>
                                            </CollapseContainer>
                                        </div>
                                    </div>
                                )
                            }
                            return (
                                <div
                                className={'col-12 p-0 mt-3'}
                                key={index}>
                                    <div style={{
                                        ...Appearance.styles.unstyledPanel(),
                                        border: 'none',
                                        display: 'flex',
                                        flexDirection: 'row',
                                        minWidth: 0,
                                        height: '100%',
                                        padding: 24,
                                        width: '100%'
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            flexGrow: 1,
                                            overflow: 'hidden'
                                        }}>
                                            <span style={{
                                                ...Appearance.textStyles.header(),
                                                fontFamily: 'Zen Dots',
                                                marginBottom: 4,
                                                whiteSpace: 'normal'
                                            }}>{item.title.toUpperCase()}</span>
                                            <span style={{
                                                ...Appearance.textStyles.defaultText(),
                                                whiteSpace: 'normal'
                                            }}>{item.body}</span>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </AnimatedElement>
        )
    }

    const getHeader = () => {
        return (
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            useContentDivider={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
            }}>
                <PanelContentContainer
                body={'Join our network of estate planners, retirement communities, and insurance providers to offer comprehensive digital legacy protection while earning recurring commissions. Our platform provides account management, remote and in-person data wiping, legacy messaging, and emergency protocols to protect your clients\' digital assets.'}
                buttons={getContainerButtons()}
                title={'Partnership Program'} />
            </AnimatedElement>
        )
    }

    const getProcess = () => {

        let items = [{
            description: 'Once your account is approved, you\'ll instantly get access to your personalized QR code and referral link in your dashboard.',
            image: 'images/partnerships-refer-generate.png',
            title: 'Generate'
        },{
            description: 'Share your unique link or QR code with clients via email, social media, your website, or printed materials.',
            image: 'images/partnerships-refer-share.png',
            title: 'Share'
        },{
            description: 'When clients sign up through your link, they\'re automatically tracked to your account and you earn commissions for years to come.',
            image: 'images/partnerships-refer-earn.png',
            title: 'Earn'
        }];

        return (
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            useContentDivider={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
            }}>
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    maxWidth: 1750,
                    overflow: 'hidden',
                    textAlign: 'center',
                    width: '100%'
                }}>
                    <PanelContentContainer
                    body={`We've made referring customers as simple as possible - just share your unique link or QR code. Our partner dashboard provides real-time tracking of all referrals, conversions, and commission earnings.`}
                    title={'Simple Referral Process'} />
                    <div className={'row mx-0 my-0 mt-xl-3'}>
                        {items.map((item, index) => {
                            if(Utils.isMobile() === true) {
                                return (
                                    <div 
                                    className={'col-12 mx-0 mt-2 p-0'}
                                    key={index}>
                                        <div 
                                        className={'p-4'}
                                        style={{
                                            backgroundColor: Appearance.colors.panelBackground(),
                                            display: 'flex',
                                            flexDirection: 'column',
                                            height: '100%',
                                            textAlign: 'left',
                                            width: '100%'
                                        }}>
                                            <span style={{
                                                ...Appearance.textStyles.heavyHeader(),
                                                fontFamily: 'Zen Dots',
                                                marginBottom: 8,
                                                whiteSpace: 'normal'
                                            }}>{item.title.toUpperCase()}</span>
                                            <span style={{
                                                ...Appearance.textStyles.defaultText(),
                                                flexGrow: 1,
                                                whiteSpace: 'normal'
                                            }}>{item.description}</span>
                                        </div>
                                    </div>
                                )
                            }
                            return (
                                <div 
                                className={'col m-0 p-2'}
                                key={index}>
                                    <div 
                                    className={'p-4'}
                                    style={{
                                        ...Appearance.styles.unstyledPanel(),
                                        alignItems: 'flex-start',
                                        border: 'none',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        height: '100%',
                                        textAlign: 'left',
                                        width: '100%'
                                    }}>
                                        <img 
                                        src={item.image}
                                        style={{
                                            height: 50,
                                            marginBottom: 16,
                                            objectFit: 'contain',
                                            width: 50
                                        }} />
                                        <span style={{
                                            ...Appearance.textStyles.heavyHeader(),
                                            fontFamily: 'Zen Dots',
                                            marginBottom: 4,
                                            whiteSpace: 'normal'
                                        }}>{item.title.toUpperCase()}</span>
                                        <span style={{
                                            ...Appearance.textStyles.defaultText(),
                                            flexGrow: 1,
                                            whiteSpace: 'normal'
                                        }}>{item.description}</span>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </AnimatedElement>
        )
    }

    const getTierComponents = () => {
        return (
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            useContentDivider={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
            }}>
                <PanelContentContainer
                body={`Our tiered commission structure is designed to grow with you. As a LifeWipe partner, you'll earn commissions based on the number of monthly active users you refer—and the more clients you bring in, the higher your payout. It's a scalable model that rewards long-term engagement and trust.`}
                title={'Partnership Tiers'} />

                <div 
                className={'row w-100 mx-0 mt-3 mb-0 flex-xl-nowrap'}
                style={{
                    maxWidth: 1250
                }}>
                    {getPartnershipTiers().map((item, index) => {
                        if(Utils.isMobile() === true) {
                            return (
                                <div 
                                className={`col-12 col-xl px-4 pt-${index === 0 ? 2 : 4} pb-0`}
                                key={index}>
                                    <div style={{
                                        ...Appearance.styles.unstyledPanel(),
                                        border: 'none',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        height: '100%',
                                        width: '100%'
                                    }}>
                                        <CollapseContainer 
                                        defaultValue={true} 
                                        header={(
                                            <div style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                padding: 16,
                                                textAlign: 'left',
                                                width: '100%'
                                            }}>
                                                <span style={{
                                                    ...Appearance.textStyles.heavyHeader(),
                                                    color: Appearance.colors.primary(),
                                                    fontFamily: 'Zen Dots',
                                                    marginBottom: 0
                                                }}>{item.title.toUpperCase()}</span>
                                                <span style={{
                                                    ...Appearance.textStyles.defaultText()
                                                }}>{item.description}</span>
                                            </div>
                                        )}>
                                            <div style={{
                                                borderTop: `1px solid ${Appearance.colors.divider()}`,
                                                flexGrow: 1,
                                                height: '100%',
                                                width: '100%'
                                            }}>
                                                {item.benefits.map((text, index) => {
                                                    return (
                                                        <div 
                                                        key={index}
                                                        style={{
                                                            alignItems: 'center',
                                                            borderBottom: `1px solid ${Appearance.colors.divider()}`,
                                                            display: 'flex',
                                                            flexDirection: 'row',
                                                            padding: 12,
                                                            textAlign: 'left',
                                                            width: '100%'
                                                        }}>
                                                            <img 
                                                            src={window.theme === 'dark' ? 'images/checkmark-badge-dark.png' : 'images/checkmark-badge-light.png'}
                                                            style={{
                                                                height: 25,
                                                                marginRight: 8,
                                                                objectFit: 'contain',
                                                                width: 25 
                                                            }} />
                                                            <span style={{
                                                                ...Appearance.textStyles.defaultTextSmall()
                                                            }}>{text}</span>
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                            <Button
                                            color={'primary'}
                                            label={`Apply for ${item.title}`}
                                            onClick={onApplyForTierClick.bind(this, item)}
                                            style={{
                                                borderRadius: 0
                                            }} 
                                            type={'large'} />
                                            
                                        </CollapseContainer>
                                    </div>
                                </div>
                            )
                        }
                        return (
                            <div 
                            className={'col-12 col-xl p-0 p-xl-2 mb-3 mb-xl-0'}
                            key={index}>
                                <div style={{
                                    ...Appearance.styles.unstyledPanel(),
                                    border: 'none',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    height: '100%',
                                    width: '100%'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        padding: 24,
                                        textAlign: 'left',
                                        width: '100%'
                                    }}>
                                        <span style={{
                                            ...Appearance.textStyles.heavyHeader(),
                                            color: Appearance.colors.primary(),
                                            fontFamily: 'Zen Dots',
                                            marginBottom: 0
                                        }}>{item.title.toUpperCase()}</span>
                                        <span style={{
                                            ...Appearance.textStyles.defaultText(),
                                            marginBottom: 24
                                        }}>{item.description}</span>
                                        <AltBadge
                                        content={{
                                            color: Appearance.colors.grey(),
                                            text: `${item.range} Monthly Users`
                                        }} 
                                        style={{
                                            display: 'inline-flex' 
                                        }} />
                                    </div>
                                    <div style={{
                                        borderTop: `1px solid ${Appearance.colors.divider()}`,
                                        flexGrow: 1,
                                        height: '100%',
                                        width: '100%'
                                    }}>
                                        {item.benefits.map((text, index) => {
                                            return (
                                                <div 
                                                key={index}
                                                style={{
                                                    alignItems: 'center',
                                                    borderBottom: `1px solid ${Appearance.colors.divider()}`,
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                    padding: 12,
                                                    textAlign: 'left',
                                                    width: '100%'
                                                }}>
                                                    <img 
                                                    src={window.theme === 'dark' ? 'images/checkmark-badge-dark.png' : 'images/checkmark-badge-light.png'}
                                                    style={{
                                                        height: 25,
                                                        marginRight: 8,
                                                        objectFit: 'contain',
                                                        width: 25 
                                                    }} />
                                                    <span style={{
                                                        ...Appearance.textStyles.defaultTextSmall()
                                                    }}>{text}</span>
                                                </div>
                                            )
                                        })}
                                    </div>
                                    <Button
                                    color={'primary'}
                                    label={`Apply for ${item.title}`}
                                    onClick={onApplyForTierClick.bind(this, item)}
                                    style={{
                                        borderRadius: 0
                                    }} 
                                    type={'large'} />
                                </div>
                            </div>
                        )
                    })}
                </div>
            </AnimatedElement>
        )
    }

    const startApplication = (tier, user) => {
        utils.layer.open({
            abstract: Abstract.create({
                object: user,
                type: 'users',
            }),
            Component: NewPartnershipApplication.bind(this, { tier }),
            id: 'new_partnership_application'
        });
    }

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%'
        }}>
            {getHeader()}
            {getBenefits()}
            {getTierComponents()}
            {getDescription()}
            {getProcess()}
            {getFAQ()}
        </div>
    )
}

export const ScheduleDemo = ({ index, options, utils }) => {

    const layerID = 'schedule_demo';

    const [edits, setEdits] = useState({});
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);

    const getFields = () => {
        return [{
            collapsed: null,
            key: 'details',
            lastItem: true,
            style: {
                border: 'none'
            },
            items: [{
                component: 'textfield',
                key: 'first_name',
                onChange: text => onUpdateTarget({ first_name: text }),
                title: 'First Name',
                value: edits.first_name
            },{
                component: 'textfield',
                key: 'last_name',
                onChange: text => onUpdateTarget({ last_name: text }),
                title: 'Last Name',
                value: edits.last_name
            },{
                component: 'textfield',
                key: 'email_address',
                onChange: text => onUpdateTarget({ email_address: text }),
                title: 'Email Address',
                value: edits.email_address
            },{
                component: 'textfield',
                key: 'phone_number',
                onChange: text => onUpdateTarget({ phone_number: text }),
                props: {
                    format: 'phone_number'
                },
                title: 'Phone Number',
                value: edits.phone_number
            },{
                component: 'textview',
                key: 'description',
                onChange: text => onUpdateTarget({ description: text }),
                title: 'How Can We Help?',
                value: edits.description
            }]
        }];
    }

    const onSubmitClick = async () => {
        try {

            // verify that required fields were filled out
            await validateRequiredFields(getFields);

            // set loading flag and submit request to server
            setLoading('submit');
            await Request.post(utils, '/utils/', {
                ...edits,
                type: 'new_demo_schedule_request'
            });

            // show confirmation alert
            setLoading(false);
            utils.alert.show({
                title: 'All Done!',
                message: 'Your demonstration request has been submitted. We will reach out within the next 48 hours to get on your calendar.',
                onClick: setLayerState.bind(this, 'close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue submitting your demonstration request. ${e.message || 'An unknown error occurred'}`,
                origin: 'schedule_demo.on_submit_click'
            });
        }
    }


    const onUpdateTarget = props => {
        setEdits(edits => ({ ...edits, ...props }));
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        },{
            color: 'primary',
            id: 'submit',
            loading: loading === 'submit',
            onClick: onSubmitClick,
            text: 'Submit'
        }];
    }

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            removePadding: true
        }}>
            <div style={{
                borderBottom: `1px solid ${Appearance.colors.divider()}`,
                padding: 18,
                width: '100%'
            }}>
                <LayerItem 
                collapsed={null}
                lastItem={true}
                title={'Request a Demonstration'}>
                    <span style={{
                        ...Appearance.textStyles.defaultText()
                    }}>{`We'd love to show you what we're building! Just fill out the fields below to request a quick demo. It's the best way to see how everything works and ask any questions you've got. Once you hit submit, we'll be in touch to set up a time that works for you.`}</span>
                </LayerItem>
            </div>
            <AltFieldMapper
            fields={getFields()}
            utils={utils} />
        </Layer>
    )
}